/**
 * One WebSocket connection with bounded reconnects (ZLAYT-1421).
 *
 * The provider used to keep the socket in React state and reconnect from a
 * useEffect keyed on a callback that depended on that state. Every close set
 * the state, which rebuilt the callback, which reconnected at once, skipping
 * the backoff timer; a close from an older socket also wiped the reference to
 * the live one. Against a server that accepts and then closes (what the
 * backend consumer does for a token it cannot authenticate) that reconnected
 * many times a second, and because onopen reset the attempt counter the log
 * always read "attempt 1/10" and never stopped.
 *
 * Here the socket lives outside React: only the current socket can schedule a
 * reconnect, the delay doubles up to 30s, and the attempt counter resets only
 * once a connection has stayed up for stableAfterMs.
 *
 * ZLAYT-1447: before each connect ensureFreshToken gets a chance to refresh
 * the access token, since the URL carries it and only the HTTP layer used to
 * refresh it. After the server closes with 4401 (token did not authenticate)
 * the next attempt forces a refresh.
 */
export interface ReconnectingSocketOptions {
  /** The URL to connect to, or null when there is no token to connect with. */
  getUrl: () => string | null;
  onMessage: (data: string) => void;
  WebSocketImpl?: typeof WebSocket;
  maxAttempts?: number;
  stableAfterMs?: number;
  maxDelayMs?: number;
  log?: (...args: unknown[]) => void;
  /**
   * Runs before every connect; the socket opens once it settles, whether it
   * resolves or rejects. force is true after a WS_CLOSE_UNAUTHENTICATED close.
   */
  ensureFreshToken?: (force: boolean) => Promise<void>;
}

/** The backend's close code for a token it could not authenticate. */
export const WS_CLOSE_UNAUTHENTICATED = 4401;

const OPEN = 1;
const CONNECTING = 0;

export class ReconnectingSocket {
  private ws: WebSocket | null = null;

  private attempts = 0;

  private stopped = true;

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  private stableTimer: ReturnType<typeof setTimeout> | null = null;

  /** A token check is running; the socket opens when it settles. */
  private preparing = false;

  /** The last close was a 4401: refresh before the next attempt. */
  private authRejected = false;

  constructor(private readonly opts: ReconnectingSocketOptions) {}

  start() {
    this.stopped = false;
    this.ensureConnected();
  }

  stop() {
    this.stopped = true;
    this.clearTimers();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      ws.onmessage = null;
      ws.close();
    }
  }

  /** Connect if there is no open or opening socket and no reconnect pending. */
  ensureConnected() {
    if (this.stopped || this.reconnectTimer || this.preparing) return;
    const state = this.ws?.readyState;
    if (state === OPEN || state === CONNECTING) return;
    this.connect();
  }

  private clearTimers() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.stableTimer) clearTimeout(this.stableTimer);
    this.reconnectTimer = null;
    this.stableTimer = null;
  }

  private connect() {
    const { ensureFreshToken } = this.opts;
    if (!ensureFreshToken) {
      this.open();
      return;
    }
    const force = this.authRejected;
    this.authRejected = false;
    this.preparing = true;
    let check: Promise<void>;
    try {
      check = ensureFreshToken(force);
    } catch (error) {
      check = Promise.reject(error);
    }
    check
      .catch(() => {
        // Connect anyway: the server will refuse a bad token with 4401, and
        // the HTTP layer owns signing the user out.
      })
      .finally(() => {
        this.preparing = false;
        if (!this.stopped) this.open();
      });
  }

  private open() {
    const url = this.opts.getUrl();
    if (!url) return;
    const Impl = this.opts.WebSocketImpl || WebSocket;
    const log = this.opts.log || (() => {});
    let ws: WebSocket;
    try {
      ws = new Impl(url);
    } catch (error) {
      log('[WebSocket] Failed to create connection', error);
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      if (this.ws !== ws) return;
      log('[WebSocket] Connected');
      if (this.stableTimer) clearTimeout(this.stableTimer);
      this.stableTimer = setTimeout(() => {
        this.attempts = 0;
        this.stableTimer = null;
      }, this.opts.stableAfterMs ?? 30_000);
    };
    ws.onmessage = (event: MessageEvent) => {
      if (this.ws !== ws) return;
      this.opts.onMessage(String(event.data));
    };
    ws.onclose = (event: CloseEvent) => {
      // A close from a socket we already replaced must not touch the live one.
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.stableTimer) clearTimeout(this.stableTimer);
      this.stableTimer = null;
      log('[WebSocket] Closed', { code: event?.code, reason: event?.reason });
      if (event?.code === WS_CLOSE_UNAUTHENTICATED) this.authRejected = true;
      if (!this.stopped) this.scheduleReconnect();
    };
  }

  private scheduleReconnect() {
    const max = this.opts.maxAttempts ?? 10;
    const log = this.opts.log || (() => {});
    if (this.attempts >= max) {
      log('[WebSocket] Giving up after repeated failures; reload to retry.');
      return;
    }
    this.attempts += 1;
    const delay = Math.min(
      1000 * 2 ** (this.attempts - 1),
      this.opts.maxDelayMs ?? 30_000
    );
    log(
      `[WebSocket] Reconnecting in ${delay}ms (attempt ${this.attempts}/${max})`
    );
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.ensureConnected();
    }, delay);
  }
}
